// frontend/src/services/api.js

import axios from 'axios';
import config from '../config';

const api = axios.create({
    // Origem única da URL da API: definida por REACT_APP_API_URL no build.
    // Ver src/config.js.
    baseURL: config.API_BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];

// Ponto unico de encerramento de sessao: limpa as credenciais e manda pro login.
const encerrarSessao = (motivo) => {
    console.log(`🔄 Encerrando a sessão: ${motivo}. Redirecionando para o login...`);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setTimeout(() => {
        window.location.href = '/login';
    }, 100);
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Adicionar interceptor para incluir o token nas requisições
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para respostas - lidar com tokens expirados
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Já tentamos renovar e mesmo assim voltou 401: a sessão acabou de fato.
        if (error.response?.status === 401 && originalRequest?._retry) {
            encerrarSessao('a renovação do token não resolveu o 401');
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && originalRequest) {
            // QUALQUER 401 merece uma tentativa de renovação antes de derrubar a
            // sessão. Antes, só `TOKEN_EXPIRED` tentava renovar: um `TOKEN_INVALID`
            // — assinatura que não confere, como acontece quando a API reinicia com
            // outro JWT_SECRET — mandava o usuário direto para o login, perdendo o
            // que ele estivesse preenchendo.
            if (!isRefreshing) {
                isRefreshing = true;
                originalRequest._retry = true;

                try {
                    const refreshToken = localStorage.getItem('refreshToken');
                    
                    if (!refreshToken) {
                        throw new Error('Refresh token não encontrado');
                    }

                    const response = await axios.post(`${config.API_BASE_URL}/auth/refresh-token`, {
                        refreshToken: refreshToken
                    });

                    const newToken = response.data.token;
                    localStorage.setItem('token', newToken);
                    
                    // Processar fila de requisições que falharam
                    processQueue(null, newToken);
                    isRefreshing = false;

                    // Repetir a requisição original com o novo token
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);

                } catch (refreshError) {
                    console.error('❌ Erro ao renovar token via API:', refreshError);
                    processQueue(refreshError, null);
                    isRefreshing = false;
                    encerrarSessao('a renovação do token falhou');
                    return Promise.reject(refreshError);
                }
            } else {
                // Já há uma renovação em andamento: entra na fila e é repetida
                // com o token novo assim que ela terminar.
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }
        }

        return Promise.reject(error);
    }
);

export default api;

// Função específica para sincronização manual de nota fiscal por chave
export const sincronizarNotaPorChave = async (chaveAcesso) => {
    try {
        const response = await api.post('/notasfiscais/sincronizar-chave', {
            chaveAcesso: chaveAcesso
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};
